import { Notification } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Flex } from 'src/design/atoms/Flex';
import { Group } from 'src/design/atoms/Group';
import { LoadingOverlay } from 'src/design/atoms/LoadingOverlay';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Select } from 'src/design/atoms/Select';
import { Table } from 'src/design/molecules/Table';
import { Textarea } from 'src/design/atoms/Textarea';
import { useFormik } from 'formik';
import moment from 'moment';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import {
  GetTimesheetRequest,
  JobDto,
  JobUserDto,
  TimesheetDto,
  TimesheetRequestDto,
  TimesheetRowDto,
} from 'src/api/FiretrolApiClient';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import { useTimesheet } from 'src/api/queries/timesheets/useTimesheet';
import {
  useSendTimesheetNotification,
  useUpsertTimesheet,
} from 'src/api/queries/timesheets/useTimesheetMutations';
import { WorkflowButtons } from 'src/app/workflows/WorkflowButtons/WorkflowButtons';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { TimesheetHistoryPanel } from 'src/app/timesheets/TimesheetHistoryPanel';
import * as Yup from 'yup';
import { TimesheetWeekHoursPanel } from 'src/app/timesheets/TimesheetWeekHoursPanel';
import { buildTimesheetWeekHours } from 'src/app/timesheets/buildTimesheetWeekHours';
import {
  TIMESHEET_GRID_MIN_WIDTH,
  timesheetGridColumns,
  timesheetGridLabelColumn,
  timesheetGridTableStyle,
} from 'src/app/timesheets/timesheetGrid';
import { useTimesheetWeekHours } from 'src/api/queries/timesheets/useTimesheetWeekHours';
import { ScaleBadge } from 'src/app/timesheets/ScaleBadge';
import { TimesheetRowRate } from 'src/routes/Jobs/Timesheets/components/TimesheetCard/TimesheetRowRate';
import { useAuth } from 'src/hooks/useAuth';
import { useOrganization } from 'src/hooks/useOrganization';
import { useTimesheetCalculations } from 'src/hooks/useTimesheetCalculations';
import {
  convertAccountsToTreeData,
  getErrorMessage,
  logger,
  traverseWbsTree,
  formatDateTime,
  formatHoursCompact,
  formatCurrency,
} from 'src/utils';
import { WbsTreeData } from 'src/routes/Jobs/WBS/components/types';
import { CostCodeSelector } from 'src/routes/Jobs/Timesheets/components/CostCodeSelector';
import { HoursInput } from 'src/routes/Jobs/Timesheets/components/HoursInput';
import { ServiceWorkOrderSelect } from 'src/routes/Jobs/Timesheets/components/ServiceWorkOrderSelect';
import { tempOptions } from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import { formatWorkWeekId } from 'src/utils/workWeek';
import { ISignaturePad, loadStoredSignature } from 'src/utils/storedSignature';

// Visitor function to build cost code paths for timesheet options
// Only include accounts that are:
// 1. Leaf nodes (no children)
// 2. Active
// 3. Account type is 'labor' (for time tracking)
const buildCostCodePaths = (account: WbsTreeData, parentPath: string) => {
  if (account.isLeaf && account.active && account.accountType === 'labor') {
    return {
      path: parentPath.slice(0, -3), // Remove trailing slash and space
      account: account,
    };
  }
  return null;
};

// Visitor function to build cost code to account ID mapping
const buildCostCodeMapping = (account: WbsTreeData, parentPath: string) => {
  if (account.isLeaf && account.active && account.accountType === 'labor') {
    return {
      costCode: account.code,
      accountId: account.id,
    };
  }
  return null;
};

const validationSchema = Yup.object().shape({
  updateReason: Yup.string().when('isComplete', {
    is: true,
    then: (schema) =>
      schema
        .required('Update reason is required for approved timesheets')
        .min(5, 'Update reason must be at least 5 characters'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

interface TimesheetCardProps {
  selectedJob: JobDto;
  weekId: string;
  year: string;
  team: JobUserDto[];
  userId: string;
  showNextButtons?: boolean;
  onSaveOnlyComplete?: (timesheet: TimesheetDto) => void;
}

export const TimesheetCard: React.FC<TimesheetCardProps> = ({
  selectedJob,
  weekId,
  year,
  userId,
  team,
  showNextButtons = true,
  onSaveOnlyComplete,
}) => {
  const params: any = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, scopes, isAdmin } = useAuth();
  const { payCodes } = useOrganization();
  const [error, setError] = React.useState<string | null>(null);
  const [showNotification, setShowNotification] = React.useState(false);
  const [selectedOptions, setSelectedOptions] = React.useState<string[] | null>(
    null,
  );
  const [timesheetRows, setTimesheetRows] = React.useState<TimesheetRowDto[]>(
    [],
  );
  const [showAddOptions, setShowAddOptions] = React.useState(false);
  const signatureRef = React.useRef<ISignaturePad>(null);
  const [timesheetValues, setTimesheetValues] =
    React.useState<TimesheetDto | null>(null);
  // Holds the identity of the timesheet with unsaved row edits. A background
  // refetch of that same timesheet must not reseed local state over them;
  // switching user/week/job is a different identity and reseeds normally.
  const editedKeyRef = React.useRef<string | null>(null);
  const timesheetKey = `${selectedJob.id}:${userId}:${year}:${weekId}`;
  const markEdited = () => {
    editedKeyRef.current = timesheetKey;
  };

  const isServiceJobType = selectedJob.jobType === 'service';

  const { data: wbsResponse, isLoading: loadingWBS } = useJobWbs(
    isServiceJobType ? undefined : selectedJob.id,
  );

  // Only active labor accounts the job applies can take timesheet hours.
  const wbsTreeData = React.useMemo(
    () =>
      wbsResponse
        ? convertAccountsToTreeData(
            wbsResponse.wbsAccount,
            (account) =>
              !account.archived &&
              account.active &&
              account.accountType === 'labor' &&
              account.apply,
          )
        : [],
    [wbsResponse],
  );

  const costCodePathOptions = React.useMemo(
    () => traverseWbsTree(wbsTreeData, buildCostCodePaths),
    [wbsTreeData],
  );

  const costCodePaths = React.useMemo(() => {
    const map = new Map<string, string>();
    costCodePathOptions.forEach((option) => {
      map.set(option.account.id, option.path);
    });
    return map;
  }, [costCodePathOptions]);

  const costCodeOptions: WbsTreeData[] = React.useMemo(
    () => costCodePathOptions.map((option) => option.account),
    [costCodePathOptions],
  );

  const timesheetQuery = useTimesheet(
    {
      jobId: selectedJob.id,
      userId: Number(userId),
      weekId: Number(weekId),
      year: Number(year),
    },
    { enabled: isServiceJobType || wbsTreeData.length > 0 },
  );
  const loadingTimesheet = timesheetQuery.isLoading;
  const loadError = timesheetQuery.error
    ? getErrorMessage(timesheetQuery.error, 'Failed to load timesheet')
    : null;

  const upsertTimesheet = useUpsertTimesheet();
  const sendNotification = useSendTimesheetNotification();
  const saving = upsertTimesheet.isPending || sendNotification.isPending;

  const isTimesheetAdmin = React.useMemo(() => {
    return isAdmin || scopes.includes('jobs_timesheets_admin');
  }, [scopes, isAdmin]);

  // editable === false locks data inputs at this step; transitions remain functional
  const isEditable = React.useMemo(() => {
    if (isTimesheetAdmin) return true;
    if (!timesheetValues?.workflow) return true;
    const stepIndex = timesheetValues.stepIndex;
    if (stepIndex < 0) return true;
    const step = timesheetValues.workflow.steps[stepIndex];
    return step?.editable !== false;
  }, [isTimesheetAdmin, timesheetValues?.workflow, timesheetValues?.stepIndex]);

  const isServiceJob = React.useMemo(() => {
    return selectedJob.jobType === 'service';
  }, [selectedJob.jobType]);

  const isTimesheetOwner = React.useMemo(() => {
    // Check if current user is the owner of this timesheet
    // Compare with both the userId prop and the timesheetValues.userId
    const timesheetUserId = timesheetValues?.userId;
    if (timesheetUserId && user?.id) {
      return user.id === timesheetUserId;
    }
    // For new timesheets, compare with the userId prop
    if (userId && user?.id) {
      return user.id.toString() === userId;
    }
    return false;
  }, [user?.id, userId, timesheetValues?.userId]);

  const formik = useFormik({
    initialValues: {
      updateReason: '',
      isComplete: timesheetValues?.isComplete ?? false,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: async () => {
      logger.debug('ignored');
    },
  });

  React.useEffect(() => {
    loadStoredSignature(signatureRef.current, timesheetValues?.signature);
    loadStoredSignature(signatureRef.current, timesheetValues?.signatureString);
  }, [timesheetValues?.signature, timesheetValues?.signatureString]);

  // The server DTO is the source of truth; the editor keeps a local copy so
  // in-progress row edits survive between saves.
  React.useEffect(() => {
    if (timesheetQuery.data === undefined || !user) return;
    if (editedKeyRef.current === timesheetKey) return;

    setTimesheetRows(validateAndUpdateTimesheetRows(timesheetQuery.data.rows));
    setTimesheetValues(timesheetQuery.data);
  }, [timesheetQuery.data, wbsTreeData, user?.id, timesheetKey]);

  const handleClose = () => {
    setShowNotification(false);
  };

  const persistTimesheet = async (timesheet: TimesheetRequestDto) => {
    setError(null);
    try {
      const result = await upsertTimesheet.mutateAsync(timesheet);
      editedKeyRef.current = null;
      setTimesheetValues(result);
      setTimesheetRows(result.rows);
      setShowNotification(true);
      if (onSaveOnlyComplete) {
        onSaveOnlyComplete(result);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save timesheet'));
    }
  };

  const saveTimesheet = async () => {
    if (!selectedJob || !user || !timesheetValues) return null;
    await persistTimesheet({
      ...timesheetValues,
      isComplete:
        !timesheetValues.workflow?.steps[timesheetValues.stepIndex + 1],
      requestedRows: timesheetRows,
      updateReason: formik.values.updateReason || undefined,
    });
  };

  const submitTimesheet = async (newStatus: string, newStepIndex: number) => {
    if (!selectedJob || !user || !timesheetValues) return null;
    await persistTimesheet({
      ...timesheetValues,
      status: newStatus,
      stepIndex: newStepIndex,
      isComplete: !timesheetValues.workflow?.steps[newStepIndex + 1],
      requestedRows: timesheetRows,
      updateReason: formik.values.updateReason || undefined,
    });
  };

  const resendTimesheetEmail = async () => {
    if (!timesheetValues) return;
    setError(null);
    try {
      const request: GetTimesheetRequest = {
        timesheetId: timesheetValues.id,
      };
      await sendNotification.mutateAsync(request);
      setShowNotification(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to resend email notification'));
    }
  };

  // Rows saved before the WBS link existed carry only a cost code; map them
  // back onto their account so the grid can resolve a charge code.
  const validateAndUpdateTimesheetRows = (rows: any[]) => {
    if (!rows || rows.length === 0) return rows;

    try {
      logger.debug('WBS Integration: Validating timesheet rows', {
        count: rows.length,
      });

      // Create a mapping of cost codes to WBS account IDs using the generic traversal
      const costCodeMappings = traverseWbsTree(
        wbsTreeData,
        buildCostCodeMapping,
      );
      const costCodeToAccountId = new Map<string, string>();

      costCodeMappings.forEach((mapping) => {
        costCodeToAccountId.set(mapping.costCode, mapping.accountId);
      });

      logger.debug('WBS Integration: Built cost code mapping', {
        entries: costCodeToAccountId.size,
      });

      // Transform rows.
      let updatedCount = 0;
      const updatedRows = rows.map((row) => {
        // Update rows that don't have WBS account IDs
        if (!row.wbs_account_id && row.costCode) {
          const accountId = costCodeToAccountId.get(row.costCode);
          if (accountId) {
            updatedCount++;
            row.wbs_account_id = accountId;
          }
        }

        // Transform number fields to numbers.
        row.sun = Number(row.sun);
        row.mon = Number(row.mon);
        row.tue = Number(row.tue);
        row.wed = Number(row.wed);
        row.thu = Number(row.thu);
        row.fri = Number(row.fri);
        row.sat = Number(row.sat);
        row.total = Number(row.total);

        return row;
      });

      logger.debug('WBS Integration: Updated rows with WBS account IDs', {
        count: updatedCount,
      });
      return updatedRows;
    } catch (err) {
      logger.error('WBS Integration: Error validating timesheet rows', { err });
      return rows;
    }
  };

  const selectedWeek = React.useMemo(
    () =>
      tempOptions.find(
        (o) => o.id === formatWorkWeekId(Number(year), Number(weekId)),
      ),
    [year, weekId],
  );

  const selectedTeamMember = React.useMemo(
    () => team.find((o) => o.userId === Number(userId)),
    [userId, team],
  );

  const nextTeamMember = React.useMemo(() => {
    const teamUsers = team.map((t) => t.userId);
    const current = teamUsers.indexOf(Number(userId));
    const next = current + 1;
    if (team[next]) {
      return team[next].userId;
    } else {
      return null;
    }
  }, [userId, team]);

  const prevTeamMember = React.useMemo(() => {
    const teamUsers = team.map((t) => t.userId);
    const current = teamUsers.indexOf(Number(userId));
    const next = current - 1;
    if (team[next]) {
      return team[next].userId;
    } else {
      return null;
    }
  }, [userId, team]);

  const next = () => {
    if (nextTeamMember) {
      const nextUrl = `timesheets/user/${nextTeamMember}/week/${year}/${weekId}`;
      navigate(location.pathname.replace(params['*'] as string, nextUrl));
    }
  };

  const prev = () => {
    if (prevTeamMember) {
      const nextUrl = `timesheets/user/${prevTeamMember}/week/${year}/${weekId}`;
      navigate(location.pathname.replace(params['*'] as string, nextUrl));
    }
  };

  const handleChange = (value: string[] | null) => {
    setSelectedOptions(value);
  };

  const addToTimesheet = () => {
    if (!selectedOptions || !selectedWeek || payCodes.length === 0) return;

    selectedOptions.forEach((selectedOption) => {
      // Find the WBS account that matches the selected ID
      const wbsAccount = costCodeOptions.find(
        (account) => account.id === selectedOption,
      );

      if (!wbsAccount) return;

      const usedPayCodeIds = timesheetRows
        .filter((row) => row.wbsAccountId === wbsAccount.id)
        .map((row) => row.payCodeId);

      const availablePayCode = payCodes.find(
        (pc) => !usedPayCodeIds.includes(pc.id),
      );

      if (!availablePayCode) return;

      const newTimesheetRow: TimesheetRowDto = {
        id: '00000000-0000-0000-0000-000000000000',
        timesheetId: timesheetValues?.id || '',
        wbsAccountId: wbsAccount.id,
        payCodeId: availablePayCode.id,
        sun: 0,
        mon: 0,
        tue: 0,
        wed: 0,
        thu: 0,
        fri: 0,
        sat: 0,
        total: 0,
        startAt: selectedWeek.startDate,
        endAt: moment(selectedWeek.startDate).add(6, 'days').toDate(),
      };

      markEdited();
      setTimesheetRows((prevState) => [...prevState, newTimesheetRow]);
    });

    setSelectedOptions(null);
    setShowAddOptions(false);
  };

  const addServiceCall = () => {
    if (!selectedWeek || payCodes.length === 0) return;

    const defaultPayCode = payCodes[0];

    const newTimesheetRow: TimesheetRowDto = {
      id: '00000000-0000-0000-0000-000000000000',
      timesheetId: timesheetValues?.id || '',
      wbsAccountId: undefined,
      payCodeId: defaultPayCode.id,
      serviceCallId: undefined,
      serviceDescription: '',
      serviceLocation: undefined,
      sun: 0,
      mon: 0,
      tue: 0,
      wed: 0,
      thu: 0,
      fri: 0,
      sat: 0,
      total: 0,
      startAt: selectedWeek.startDate,
      endAt: moment(selectedWeek.startDate).add(6, 'days').toDate(),
    };

    markEdited();
    setTimesheetRows((prevState) => [...prevState, newTimesheetRow]);
  };

  const updateRowByIndex = (
    rowIndex: number,
    field: keyof TimesheetRowDto | 'serviceCall',
    value: string | number,
    type: 'payCode' | 'hours' | 'serviceDescription' | 'serviceCall' = 'hours',
    description?: string,
    location?: string,
  ) => {
    markEdited();
    setTimesheetRows((prevState) => {
      return prevState.map((prev, index) => {
        if (index !== rowIndex) return prev;

        if (type === 'payCode') {
          return { ...prev, payCodeId: value as string };
        }
        if (type === 'serviceCall') {
          const id = value as string | undefined;
          if (!id) {
            // Manual mode: clear linked fields, keep only freeform description
            return {
              ...prev,
              serviceCallId: undefined,
              serviceDescription: description ?? '',
              serviceLocation: undefined,
            };
          }
          return {
            ...prev,
            serviceCallId: id,
            serviceDescription: description ?? prev.serviceDescription,
            serviceLocation: location,
          };
        }
        if (type === 'serviceDescription') {
          return { ...prev, serviceDescription: value as string };
        }

        const updated = { ...prev, [field]: Number(value) };
        const total =
          updated.sat +
          updated.sun +
          updated.mon +
          updated.tue +
          updated.wed +
          updated.thu +
          updated.fri;
        return { ...updated, total };
      });
    });
  };

  const removeRowByIndex = (rowIndex: number) => {
    markEdited();
    setTimesheetRows((prevState) =>
      prevState.filter((_, index) => index !== rowIndex),
    );
  };

  const {
    totalHours,
    hoursByPayCode,
    totalsByDay,
    perDiemEligibleTotalsByDay,
  } = useTimesheetCalculations(timesheetRows, payCodes);

  const weekHoursQuery = useTimesheetWeekHours(
    Number(userId),
    Number(year),
    Number(weekId),
  );

  const weekHours = React.useMemo(
    () =>
      buildTimesheetWeekHours({
        thisHoursByPayCode: hoursByPayCode,
        thisTotalsByDay: totalsByDay,
        thisTotalHours: totalHours,
        weekHours: weekHoursQuery.data,
        excludeJobId: selectedJob.id,
        payCodes,
      }),
    [
      hoursByPayCode,
      totalsByDay,
      totalHours,
      weekHoursQuery.data,
      selectedJob.id,
      payCodes,
    ],
  );

  const costCodeOptionsMemoized = React.useMemo(() => {
    const options = costCodeOptions
      .map((wbsAccount) => {
        const usedPayCodeIds = timesheetRows
          .filter((row) => row.wbsAccountId === wbsAccount.id)
          .map((row) => row.payCodeId);

        const hasAvailablePayCode = payCodes.some(
          (pc) => !usedPayCodeIds.includes(pc.id),
        );

        if (!hasAvailablePayCode) {
          return null;
        }

        return {
          value: wbsAccount.id,
          label:
            costCodePaths.get(wbsAccount.id) ||
            (wbsAccount.code
              ? `${wbsAccount.code} - ${wbsAccount.name}`
              : wbsAccount.name),
        };
      })
      .filter((o) => o !== null);

    return options;
  }, [costCodeOptions, costCodePaths, timesheetRows, payCodes]);

  const perDiemAmount = Number(selectedJob.perDiemAmount);
  const perDiemSat = perDiemEligibleTotalsByDay.sat > 0 ? perDiemAmount : 0;
  const perDiemSun = perDiemEligibleTotalsByDay.sun > 0 ? perDiemAmount : 0;
  const perDiemMon = perDiemEligibleTotalsByDay.mon > 0 ? perDiemAmount : 0;
  const perDiemTue = perDiemEligibleTotalsByDay.tue > 0 ? perDiemAmount : 0;
  const perDiemWed = perDiemEligibleTotalsByDay.wed > 0 ? perDiemAmount : 0;
  const perDiemThu = perDiemEligibleTotalsByDay.thu > 0 ? perDiemAmount : 0;
  const perDiemFri = perDiemEligibleTotalsByDay.fri > 0 ? perDiemAmount : 0;
  const perDiemTotal =
    perDiemSat +
    perDiemSun +
    perDiemMon +
    perDiemTue +
    perDiemWed +
    perDiemThu +
    perDiemFri;

  return (
    <>
      {showNotification && (
        <Notification
          title="Timesheet Saved!"
          color="green"
          onClose={handleClose}
          style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}
        />
      )}

      <Stack gap="md" mb="md" pos="relative">
        <LoadingOverlay visible={loadingTimesheet || loadingWBS} />
        {loadError && !loadingTimesheet && !loadingWBS && (
          <Alert color="red">{loadError}</Alert>
        )}
        {error && !loadingTimesheet && !loadingWBS && (
          <Alert color="red" onClose={() => setError(null)} withCloseButton>
            {error}
          </Alert>
        )}
        {selectedWeek && (
          <Title order={3} ta="center">
            Week {weekId}, {year}: {selectedWeek.label}
          </Title>
        )}
        {showNextButtons && (
          <Group justify="center" gap="md">
            <BasicButton onClick={prev} disabled={!prevTeamMember}>
              Prev Employee
            </BasicButton>
            <BasicButton onClick={next} disabled={!nextTeamMember}>
              Next Employee
            </BasicButton>
          </Group>
        )}

        {selectedTeamMember && (
          <Group justify="space-between" wrap="wrap">
            <Text c="black">
              <b>Name:</b> {selectedTeamMember.user?.fullName}
            </Text>
            <Text c="black">
              <b>Status:</b> {timesheetValues?.status}
            </Text>
            <Text c="black">
              <b>Employee Number:</b>{' '}
              {selectedTeamMember.user?.employeeId || 'N/A'}
            </Text>
            <Group gap="xs">
              <Text c="black">
                <b>Pay Type:</b>
              </Text>
              {selectedJob.scalePayEnabled ? (
                <ScaleBadge isScale size="sm" />
              ) : (
                <Text c="black">Standard</Text>
              )}
            </Group>
            <Text c="black">
              <b>Supervisor:</b>{' '}
              {timesheetValues?.user?.supervisor?.fullName ?? 'N/A'}
            </Text>
          </Group>
        )}

        {isEditable &&
          (isServiceJob ? (
            <Box>
              <CreateButton onClick={addServiceCall}>
                Add Service Call
              </CreateButton>
            </Box>
          ) : showAddOptions ? (
            <Flex
              justify="flex-start"
              align="flex-end"
              direction="row"
              gap="sm"
            >
              {costCodeOptionsMemoized.length > 0 ? (
                <>
                  <CostCodeSelector
                    value={selectedOptions}
                    onChange={handleChange}
                    options={costCodeOptionsMemoized}
                  />
                  <CreateButton
                    onClick={addToTimesheet}
                    disabled={!selectedOptions || selectedOptions.length === 0}
                  >
                    Add to timesheet
                  </CreateButton>
                </>
              ) : (
                <Text>
                  All available cost codes have been added to the timesheet.
                </Text>
              )}
              <BasicButton
                variant="outline"
                onClick={() => setShowAddOptions(false)}
              >
                Close
              </BasicButton>
            </Flex>
          ) : (
            <Box>
              <CreateButton onClick={() => setShowAddOptions(true)}>
                Add Cost Code
              </CreateButton>
            </Box>
          ))}

        <Paper withBorder>
          <Table.ScrollContainer minWidth={TIMESHEET_GRID_MIN_WIDTH}>
            <Table style={timesheetGridTableStyle}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={timesheetGridLabelColumn(isServiceJob)}>
                    {isServiceJob ? 'Service Call' : 'Cost Code'}
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.payCode}>
                    Pay Code
                  </Table.Th>
                  {isServiceJob && (
                    <Table.Th ta="center" style={timesheetGridColumns.rate}>
                      Rate
                    </Table.Th>
                  )}
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Sat
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Sun
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Mon
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Tue
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Wed
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Thu
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.day}>
                    Fri
                  </Table.Th>
                  <Table.Th ta="center" style={timesheetGridColumns.total}>
                    Total
                  </Table.Th>
                  <Table.Th style={timesheetGridColumns.actions}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {timesheetRows.length === 0 && (
                  <Table.Tr>
                    <Table.Td>
                      {isServiceJob
                        ? 'Add Service Calls to Start'
                        : 'Add Cost Codes to Start'}
                    </Table.Td>
                  </Table.Tr>
                )}
                {/* Key saved rows by their stable server id so each row's
                  ServiceWorkOrderSelect instance follows its row when the save
                  response re-sorts them. Unsaved rows share a placeholder id, so
                  fall back to index to keep keys unique. */}
                {timesheetRows.map((row: TimesheetRowDto, rowIndex: number) => (
                  <Table.Tr
                    key={
                      row.id &&
                      row.id !== '00000000-0000-0000-0000-000000000000'
                        ? row.id
                        : `new-${rowIndex}`
                    }
                  >
                    <Table.Td style={timesheetGridLabelColumn(isServiceJob)}>
                      {isServiceJob ? (
                        <ServiceWorkOrderSelect
                          value={row.serviceCallId}
                          snapshotLabel={row.serviceDescription}
                          serviceLocation={row.serviceLocation}
                          disabled={!isEditable}
                          onChange={(id, desc, loc) =>
                            updateRowByIndex(
                              rowIndex,
                              'serviceCall',
                              id ?? '',
                              'serviceCall',
                              desc,
                              loc,
                            )
                          }
                        />
                      ) : (
                        costCodePaths.get(row.wbsAccountId) || row.wbsAccountId
                      )}
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.payCode}>
                      <Group justify="center">
                        <Select
                          size="xs"
                          px="auto"
                          value={row.payCodeId}
                          disabled={!isEditable}
                          onChange={(value) =>
                            updateRowByIndex(
                              rowIndex,
                              'payCodeId',
                              value!,
                              'payCode',
                            )
                          }
                          style={{ width: 100 }}
                          data={payCodes.map((pc) => ({
                            value: pc.id,
                            label: pc.name,
                          }))}
                        />
                      </Group>
                    </Table.Td>
                    {isServiceJob && (
                      <Table.Td ta="center" style={timesheetGridColumns.rate}>
                        <TimesheetRowRate
                          row={row}
                          userId={Number(userId)}
                          jobId={selectedJob.id}
                          isComplete={timesheetValues?.isComplete || false}
                        />
                      </Table.Td>
                    )}
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.sat)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'sat', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.sun)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'sun', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.mon)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'mon', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.tue)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'tue', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.wed)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'wed', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.thu)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'thu', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.day}>
                      <HoursInput
                        value={Number(row.fri)}
                        disabled={!isEditable}
                        onChange={(v) =>
                          updateRowByIndex(rowIndex, 'fri', v, 'hours')
                        }
                      />
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.total}>
                      {formatHoursCompact(Number(row.total))}
                    </Table.Td>
                    <Table.Td ta="center" style={timesheetGridColumns.actions}>
                      {isEditable && (
                        <DeleteActionIcon
                          requiredConfirmation={false}
                          onDelete={() => removeRowByIndex(rowIndex)}
                        />
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {timesheetRows.length > 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={isServiceJob ? 3 : 2} fw={600}>
                      Totals
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.sat)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.sun)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.mon)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.tue)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.wed)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.thu)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalsByDay.fri)}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatHoursCompact(totalHours)}
                    </Table.Td>
                  </Table.Tr>
                )}
                {selectedJob.perDiemEnabled && (
                  <Table.Tr>
                    <Table.Td colSpan={isServiceJob ? 3 : 2} fw={600}>
                      Per Diem
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemSat, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemSun, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemMon, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemTue, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemWed, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemThu, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemFri, { precise: true })}
                    </Table.Td>
                    <Table.Td ta="center">
                      {formatCurrency(perDiemTotal, { precise: true })}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        <TimesheetWeekHoursPanel
          model={weekHours}
          isServiceJob={isServiceJob}
          pending={weekHoursQuery.isPending}
          unavailable={weekHoursQuery.isError}
        />

        {timesheetValues?.isComplete && isTimesheetAdmin && (
          <Paper>
            <Textarea
              label="Update Comment"
              description="A reason is required when updating an approved timesheet. Enter a comment to explain why you are updating the timesheet."
              placeholder="Enter reason..."
              value={formik.values.updateReason}
              onChange={(e) =>
                formik.setFieldValue('updateReason', e.currentTarget.value)
              }
              error={
                formik.touched.updateReason && formik.errors.updateReason
                  ? formik.errors.updateReason
                  : null
              }
              minRows={3}
              required
            />
          </Paper>
        )}

        {timesheetValues?.isRejected && (
          <Alert color="red" title="Timesheet Rejected" mt="md" mb="md">
            Justification: {timesheetValues?.rejectJustification}
          </Alert>
        )}
        {timesheetValues?.signedBy.length > 0 && (
          <Stack gap="xs" mt="md">
            <Text size="sm">
              Signed by <b>{timesheetValues?.signedBy}</b> on{' '}
              {formatDateTime(timesheetValues?.signedOn)}
            </Text>
          </Stack>
        )}
        <SignatureCanvas
          ref={signatureRef}
          penColor="black"
          canvasProps={{
            width: 710,
            height: 120,
            className: 'sigCanvas',
            style: {
              border: '1px solid #d8d8d9',
              display:
                timesheetValues?.signatureString.length > 0 &&
                timesheetValues?.signatureString !== '[]'
                  ? 'block'
                  : 'none',
            },
          }}
        />

        {timesheetValues && timesheetValues.workflow && (
          <WorkflowButtons
            error={error}
            onSubmitClick={submitTimesheet}
            workflow={timesheetValues.workflow}
            onSaveOnlyClick={saveTimesheet}
            onVoidClick={console.log}
            stepIndex={timesheetValues.stepIndex}
            onResendEmailClick={resendTimesheetEmail}
            disabled={!formik.isValid}
            isAdmin={isTimesheetAdmin}
            isOwner={isTimesheetOwner}
          />
        )}

        {timesheetValues && timesheetValues.timesheetHistory && (
          <TimesheetHistoryPanel history={timesheetValues.timesheetHistory} />
        )}
      </Stack>
    </>
  );
};
